import CheckIcon from "@mui/icons-material/Check";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { ReactComponent as DisableIcon } from "../../../common/icons/delete.svg";
import { ReactComponent as EditIcon } from "../../../common/icons/editSmall.svg";
import { ReactComponent as SendIcon } from "../../../common/icons/send.svg";
import LockResetIcon from "@mui/icons-material/LockReset";
import { selectCustomerUserById } from "../customerUsersSlice";
import { CustomerUser, CustomerUserInviteStatus } from "../types/queries";
import { MenuItem, MenuItemKey } from "./types";
import { useContext } from "react";
import { AccessRightContext } from "../../user/AccessRightProvider";

type Action = (id: string) => void;

type Props = {
  open: boolean;
  anchorEl?: HTMLButtonElement | null;
  onClose: () => void;
  userId: string;
  onExited: () => void;
  menuActions: {
    edit: Action;
    invite: Action;
    enable: Action;
    disable: Action;
    resendInvitation: Action;
    resetPassword: Action;
  };
};

const menuItems: MenuItem[] = [
  {
    key: MenuItemKey.edit,
    icon: <EditIcon />,
  },
  {
    key: MenuItemKey.invite,
    icon: <SendIcon />,
  },
  {
    key: MenuItemKey.resendInvitation,
    icon: <SendIcon />,
  },
  {
    key: MenuItemKey.disable,
    icon: <DisableIcon />,
    color: "error.dark",
  },
  {
    key: MenuItemKey.enable,
    icon: <CheckIcon />,
  },
];

const adminMenuItems: MenuItem[] = [
  {
    key: MenuItemKey.resetPassword,
    icon: <LockResetIcon sx={{ width: 22, height: 22, margin: "-3px" }} />,
  },
];

const getFilteredMenuItems = (currentUser?: CustomerUser) => {
  // invited but not enabled
  const inactive =
    !currentUser?.account.enabled &&
    currentUser?.account.customerUserInviteStatus !==
      CustomerUserInviteStatus.UNINVITED;

  const inactiveKeys = [MenuItemKey.enable];

  if (inactive) {
    return menuItems.filter((menuItem) => inactiveKeys.includes(menuItem.key));
  }

  const uninvited =
    currentUser?.account.customerUserInviteStatus ===
      CustomerUserInviteStatus.UNINVITED ||
    !currentUser.account.customerUserInviteStatus;

  const active =
    currentUser?.account.customerUserInviteStatus ===
    CustomerUserInviteStatus.INVITE_ACCEPTED;

  const pending =
    currentUser?.account.customerUserInviteStatus ===
    CustomerUserInviteStatus.INVITE_PENDING;

  const activeKeys = [MenuItemKey.edit, MenuItemKey.disable];
  const pendingKeys = [MenuItemKey.resendInvitation];

  if (uninvited) {
    return menuItems.filter(
      (menuItem) =>
        menuItem.key === MenuItemKey.invite ||
        menuItem.key === MenuItemKey.edit,
    );
  }

  if (active) {
    return menuItems.filter((menuItem) => activeKeys.includes(menuItem.key));
  }

  if (pending) {
    return menuItems.filter(
      (menuItem) =>
        activeKeys.includes(menuItem.key) || pendingKeys.includes(menuItem.key),
    );
  }

  return [];
};

const UserActionsMenu = ({
  open,
  anchorEl,
  onClose,
  userId,
  onExited,
  menuActions,
}: Props) => {
  const { t } = useTranslation();
  const { isAdmin } = useContext(AccessRightContext);

  const currentUser = useAppSelector((state) =>
    selectCustomerUserById(state, userId),
  );

  const handleClick = (key: MenuItemKey) => {
    menuActions[key](userId);
    onClose();
  };

  const filteredMenuItems = getFilteredMenuItems(currentUser);

  const expertMenuItems = isAdmin
    ? [...filteredMenuItems, ...adminMenuItems]
    : filteredMenuItems;

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      TransitionProps={{ onExited }}
    >
      <List dense disablePadding>
        {expertMenuItems.map((menuItem) => (
          <ListItem key={menuItem.key} disablePadding>
            <ListItemButton
              sx={{
                paddingLeft: "13px",
                paddingRight: "13px",
              }}
              onClick={() => handleClick(menuItem.key)}
            >
              <ListItemIcon sx={{ minWidth: "13px", marginRight: "13px" }}>
                {menuItem.icon}
              </ListItemIcon>
              <ListItemText>
                <Typography {...(menuItem?.color && { color: menuItem.color })}>
                  {t(`customers:usersNS:${menuItem.key}`)}
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Menu>
  );
};

export default UserActionsMenu;
